import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import { LootBuyerType } from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { IPlayerService } from '../PlayerService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService/VarsService';
import {
	ILootBuyersService,
	LootBuyersSellItem,
} from './lootBuyersServiceTypes';

type LootBuyersItemsData = {
	[K in LootBuyerType]?: { [itemId: string]: number };
};

type LootBuyersItemsPayload = {
	[K in LootBuyerType]?: Array<{ itemId: string; price: number }>;
};

export class LootBuyersService implements ILootBuyersService {
	private sellItems = new Subject<LootBuyersSellItem[]>([]);
	private ownedItems = new Subject<{ [itemId: string]: number }>({});
	private updatePriceTimestamp = new Subject<number>(0);
	private itemsData: LootBuyersItemsData = {};

	constructor(
		private readonly rpc: RpcService,
		private readonly backend: BackendService,
		private readonly vars: VarsService,
		private readonly config: GameConfigService,
		player: IPlayerService,
	) {
		player.waitForCharacterSelect().then(() => {
			this.config.getConfigs().subscribe(() => {
				if (!this.config.configs) return;
				this.updateSellItems();
			});

			this.backend.addEventHandler(
				'lootBuyers:items',
				(payload: {
					items: LootBuyersItemsPayload;
					nextUpdateAt: number | undefined;
				}) => {
					this.itemsData = {} as LootBuyersItemsData;
					for (const [buyerType, itemsArray] of Object.entries(
						payload.items,
					) as [
						LootBuyerType,
						Array<{ itemId: string; price: number }> | undefined,
					][]) {
						if (itemsArray) {
							this.itemsData[buyerType] = itemsArray.reduce(
								(acc, item) => {
									acc[item.itemId] = item.price;
									return acc;
								},
								{} as { [itemId: string]: number },
							);
						}
					}

					if (payload.nextUpdateAt !== undefined) {
						this.updatePriceTimestamp.next(payload.nextUpdateAt);
					}

					this.updateSellItems();
				},
			);

			this.getCurrentLootBuyerType().subscribe((lootBuyerType) => {
				if (lootBuyerType) {
					this.fetchOwnedItems(lootBuyerType);
				}
			});
		});
	}

	private updateSellItems(): void {
		if (!this.config.configs) return;

		const configItems = this.config.configs.lootBuyers?.items ?? [];
		const inventoryConfig = this.config.configs.inventory;
		const ownedItemsData = this.ownedItems.getValue();
		const itemsWithPrices: LootBuyersSellItem[] = [];

		for (const configItem of configItems) {
			const typeItems = this.itemsData[configItem.buyerType];
			if (typeItems && typeItems[configItem.itemId] !== undefined) {
				const ownedCount = ownedItemsData[configItem.itemId];
				if (ownedCount === undefined) {
					continue;
				}

				const inventoryItem = inventoryConfig?.items.find(
					(item) => item.id === configItem.itemId,
				);
				const itemName = inventoryItem?.name || configItem.itemId;

				itemsWithPrices.push({
					id: configItem.itemId,
					name: itemName,
					price: typeItems[configItem.itemId],
					buyerType: configItem.buyerType,
					itemId: configItem.itemId,
				});
			}
		}

		this.sellItems.next(itemsWithPrices);
	}

	@notifyOnError
	private async fetchOwnedItems(lootBuyerType: LootBuyerType): Promise<void> {
		const items = await this.backend.invoke<{ [itemId: string]: number }>(
			'lootBuyers:getOwnedItems',
			{ lootBuyerType },
		);
		this.ownedItems.next(items || {});
		this.updateSellItems();
	}

	public getSellItems(): IObservable<LootBuyersSellItem[]> {
		return this.sellItems;
	}

	public getOwnedItems(): IObservable<{ [itemId: string]: number }> {
		return this.ownedItems;
	}

	public getUpdatePriceTimestamp(): IObservable<number> {
		return this.updatePriceTimestamp;
	}

	public getCurrentLootBuyerType(): IObservable<LootBuyerType | null> {
		return this.vars.get('lootBuyers:currentLootBuyerType', null);
	}

	@notifyOnError
	public async sellItem(
		lootBuyerType: LootBuyerType,
		itemId: string,
	): Promise<void> {
		await this.backend.invoke('lootBuyers:sell', {
			lootBuyerType,
			itemId,
		});
		await this.fetchOwnedItems(lootBuyerType);
	}

	@notifyOnError
	public async sellAllItems(lootBuyerType: LootBuyerType): Promise<void> {
		await this.backend.invoke('lootBuyers:sellAll', {
			lootBuyerType,
		});
		await this.fetchOwnedItems(lootBuyerType);
	}

	public exitMenu(): void {
		this.rpc.invokeClient('lootBuyers:exitMenu');
	}
}
